'use client';

import { Calendar, Clock, Flame } from '@zoomit/icons';
import { cn, Flex, Image, Link, Spacing } from '@zoomit/primitives';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { useOrigin } from '../../providers/OriginProvider';
import type { ArticleCardResponse } from '../../types/articles';
import { RelativeTime } from '../shared';
import ExternalIcon from '../shared/ExternalIcon';
import EllipticBox from './EllipticBox';

type Props = {
  article: ArticleCardResponse;
  handleEvent?: () => void;
  coverImageSizes?: string;
} & ArticleProps;

type ArticleProps = {
  variant?: 'small' | 'large' | 'full-width';
};

const ArticleImageCard = (props: Props) => {
  const { article, handleEvent, variant = 'small', coverImageSizes } = props;
  const { baseMediaUrl, baseUrl } = useOrigin();

  const href = article.type === 'External' ? article.link : `${baseUrl}/${article.slug}/`;

  return (
    <article
      className={cn(
        'aspect-primary group relative min-w-[300px] cursor-pointer overflow-hidden rounded-md md:h-auto',
        variant === 'large' ? 'md:col-span-2 md:grid md:min-w-full' : 'md:max-w-none md:min-w-auto',
        variant === 'full-width' ? 'max-w-full' : 'max-w-[300px]',
      )}
    >
      <Link
        onClick={handleEvent}
        href={href}
        rel={article.type === 'External' ? (article.linkIsFollow ? undefined : 'nofollow') : undefined}
        target={article.type === 'External' ? '_blank' : undefined}
        prefetch={article.type === 'External' ? false : true}
      >
        <>
          {article.type === 'External' && (
            <EllipticBox position="top-right">
              <ExternalIcon
                variant={'elliptic'}
                hegith={12}
                sponsorName={article.sponsorName}
                sponsorLogo={article.sponsorLogo}
              />
            </EllipticBox>
          )}
          {article.isAdvertisement && (
            <EllipticBox backgroundColor={cssVar(theme.primary1)} position="top-right">
              <Typography variant="subhead" textColor={cssVar(theme.white)}>
                تبلیغات
              </Typography>
            </EllipticBox>
          )}
          <article
            className={cn(
              'absolute top-0 right-0 z-[5] flex h-full w-full flex-col items-center bg-[rgba(25,25,35,0.5)] p-4',
              variant === 'large' ? 'p-8' : 'p-4',
            )}
          >
            <Flex className="m-auto" gap={Spacing.x2} flexWrap="nowrap" alignItems={'flex-start'}>
              {article.type === 'Internal' && article.isTrend && (
                <Flame
                  weight="fill"
                  size={variant === 'large' ? 'xl' : 'dl'}
                  style={{ flexShrink: 0 }}
                  className="mt-1 fill-white"
                />
              )}
              <Typography
                variant={variant === 'large' ? 'h2' : 'h5'}
                textColor={cssVar(theme.white)}
                lineHeight={1.8}
                tag="div"
              >
                {article.title}
              </Typography>
            </Flex>
            <Flex justifyContent="space-between" className="w-full">
              {article.type === 'Internal' && (
                <Flex alignItems="center" gap={0.4}>
                  <Clock size="sm" fill={cssVar(theme.white)} />
                  <Typography variant="subhead" className="fa" textColor={cssVar(theme.white)}>
                    مطالعه '{article.readingTime}
                  </Typography>
                </Flex>
              )}
              <Flex alignItems="center" gap={0.4}>
                <Calendar className="[&>*]:stroke-white" size="sm" fill={cssVar(theme.white)} />
                <Typography variant="subhead" className="fa" textColor={cssVar(theme.white)}>
                  <RelativeTime time={article.publishedDate} timeAgoBasis="one-month" />
                </Typography>
              </Flex>
            </Flex>
          </article>
          <Image
            placeholder="blur"
            src={`${baseMediaUrl}/${article.coverImageLink.fileName}-${article.coverImageLink.id}`}
            blurDataURL={article.coverImageLink.preview}
            alt={article.coverImageLink.alt || ''}
            fill
            sizes={coverImageSizes}
            className="object-cover duration-500"
          />
        </>
      </Link>
    </article>
  );
};

export default ArticleImageCard;
